import React from "react";

import {
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isWithinInterval,
  startOfWeek,
} from "date-fns";
import { Box } from "minimum-ui/core/Box/Box";
import { Button } from "minimum-ui/core/Button/Button";
import { Popover } from "minimum-ui/core/Popover/Popover";
import { Typography } from "minimum-ui/core/Typography/Typography";

import { color } from "../../tokens";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import { useEventEmitter } from "../useEventEmitter";
import { useDateRange } from "./useDateRange";

export interface Props {
  disabled?: boolean;
  header?: React.ReactNode;
  placeholder: string;
  value?: { start: string | null; end: string | null };
  onChange?: (
    startDate: string,
    endDate: string,
    reportingYearId?: string | undefined
  ) => void;
  translate: (key: string, options?: Record<string, any>) => string | string[];
  closePopoverEvent?: ReturnType<typeof useEventEmitter<void>>;
}

export const DateRange: React.FC<Props> = (props) => {
  const {
    disabled = false,
    header,
    placeholder,
    value = { start: null, end: null },
    onChange,
    translate,
  } = props;

  const {
    closePopoverEvent,
    currentMonth,
    tempRange,
    handleDateClick,
    handleApply,
    handleCancel,
    formatRangeDisplay,
    setCurrentMonth,
  } = useDateRange({
    closePopoverEvent: props.closePopoverEvent,
    placeholder,
    value,
    onChange,
  });

  const renderCalendar = () => {
    const startOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const endOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );
    const startDate = startOfWeek(startOfMonth);
    const endDate = endOfWeek(endOfMonth);
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    const weekDays = translate("daysShort", {
      namespace: "DEFAULT",
      returnObjects: true,
    }) as unknown as string[];

    return (
      <Box sx={{ p: 1, maxWidth: "16rem" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <IconButton
            onClick={() =>
              setCurrentMonth(
                new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth() - 1,
                  1
                )
              )
            }
            iconProps={{ icon: "chevronLeft", size: "2xs" }}
          />

          <Typography variant="titleSmall">
            {format(currentMonth, "MMMM yyyy")}
          </Typography>

          <IconButton
            onClick={() =>
              setCurrentMonth(
                new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth() + 1,
                  1
                )
              )
            }
            iconProps={{ icon: "chevronRight", size: "2xs" }}
          />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {weekDays.map((day) => (
            <Box key={day} sx={{ width: "14.28%", textAlign: "center", p: 1 }}>
              <Typography variant="bodySmall" color={color.neutral[400]}>
                {day}
              </Typography>
            </Box>
          ))}

          {days.map((day) => {
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isSelected =
              tempRange.start && isSameDay(day, tempRange.start);
            const isInRange =
              tempRange.start &&
              tempRange.end &&
              isWithinInterval(day, {
                start: tempRange.start,
                end: tempRange.end,
              });
            const isRangeStart =
              tempRange.start && isSameDay(day, tempRange.start);
            const isRangeEnd = tempRange.end && isSameDay(day, tempRange.end);

            return (
              <Box key={day.toISOString()} sx={{ width: "14.28%", mb: 1 }}>
                <Button
                  onClick={() => handleDateClick(day)}
                  sx={{
                    minWidth: "30px",
                    height: "30px",
                    width: "100%",
                    p: 0,
                    textDecoration: "none",
                    borderRadius: isRangeStart || isRangeEnd ? "0.5rem" : "0",
                    color: isCurrentMonth
                      ? isRangeStart || isRangeEnd
                        ? color.basic.white
                        : "text.primary"
                      : "text.disabled",
                    backgroundColor:
                      isRangeStart || isRangeEnd
                        ? color.neutral[500]
                        : isInRange
                        ? color.neutral[200]
                        : "transparent",
                    "&:hover": {
                      backgroundColor:
                        isRangeStart || isRangeEnd
                          ? "primary.dark"
                          : color.neutral[200],
                      borderRadius:
                        isRangeStart || isRangeEnd || !isInRange ? "0.5rem" : 0,
                    },
                  }}
                  variant={isSelected ? "contained" : "text"}
                >
                  {format(day, "d")}
                </Button>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Popover
        emitter={closePopoverEvent}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        buttonProps={{
          label: formatRangeDisplay(),
          disabled,
          startIcon: <Icon icon="calendar" />,
        }}
      >
        <Box sx={{ minWidth: "16rem" }}>
          {header && (
            <Box
              sx={{
                marginX: -1,
                paddingX: 2,
                pb: 1,
                borderBottom: `1px solid ${color.neutral[200]}`,
              }}
            >
              {header}
            </Box>
          )}
          {renderCalendar()}

          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "flex-end", p: 1 }}
          >
            <Button onClick={handleCancel} size="small" variant="text">
              {translate("cancel", { namespace: "DEFAULT" })}
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              size="small"
              disabled={!tempRange.start || !tempRange.end}
            >
              {translate("apply", { namespace: "DEFAULT" })}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
